import type { ReactNode } from "react";
import { defineStrings, formatNumber, type Locale } from "../i18n";
import { translator } from "./shared";

const strings = defineStrings(
  {
    label: "This week",
    minutes: "Time learning",
    sessions: "Sessions",
    itemsTried: "Exercises tried",
    noData: "No data yet",
  },
  {
    label: "Cette semaine",
    minutes: "Temps d’apprentissage",
    sessions: "Séances",
    itemsTried: "Exercices essayés",
    noData: "Pas encore de données",
  },
);

export interface WeeklySummaryProps {
  /** From the engine; whole, non-negative. NaN shows "No data yet". */
  readonly minutes: number;
  readonly sessions: number;
  readonly itemsTried: number;
  readonly locale: Locale;
  readonly children?: ReactNode;
}

function count(value: number): number | null {
  return Number.isFinite(value) ? Math.max(0, Math.round(value)) : null;
}

/** Three stat tiles for the week: time, sessions, exercises tried. */
export function WeeklySummary(props: WeeklySummaryProps) {
  const t = translator(strings, props.locale);
  const minutes = count(props.minutes);
  const tiles = [
    {
      key: "minutes",
      label: t("minutes"),
      value:
        minutes === null
          ? null
          : new Intl.NumberFormat(props.locale, {
              style: "unit",
              unit: "minute",
              unitDisplay: "short",
            }).format(minutes),
    },
    { key: "sessions", label: t("sessions"), value: format(props.locale, props.sessions) },
    { key: "itemsTried", label: t("itemsTried"), value: format(props.locale, props.itemsTried) },
  ] as const;
  return (
    <section className="p-week" aria-label={t("label")}>
      <dl className="p-week__tiles">
        {tiles.map((tile) => (
          <div key={tile.key} className="p-stat" data-stat={tile.key}>
            <dt className="p-stat__label">{tile.label}</dt>
            <dd className="p-stat__value" data-empty={tile.value === null ? "" : undefined}>
              {tile.value ?? t("noData")}
            </dd>
          </div>
        ))}
      </dl>
      {props.children !== undefined && <div className="p-week__extra">{props.children}</div>}
    </section>
  );
}

function format(locale: Locale, value: number): string | null {
  const whole = count(value);
  return whole === null ? null : formatNumber(locale, whole);
}
